import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from '../../../components/BreadCrumbs';
import SearchBar from '../../../components/searchBar/SearchBar';
import Dropdown from '../../../components/inputFields/DropDown';
import Table from '../../../components/tables/Table';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import B2BLeadDetail from '../../../components/leads/b2b/B2BLeadDetail';
import B2BLogInteractionModal from '../../../components/leads/b2b/B2BLogInteractionModal';
import { getB2BMyDealers, updateB2BLead, getB2BSalespersonList, toggleB2BDealerStatus } from '../../../services/b2bLeads';
import { getUserId, getUserRole, isB2bSalesperson } from '../../../helpers/local_storage_helpers';
import { B2B_OUTCOME_MAP } from '../../../constants/b2bWorkflow';
import { toast } from 'react-toastify';
import { Eye, PhoneCall } from 'lucide-react';
import { formatDateMonth } from '../../../utils/FormatDateTime';

const OutcomeBadge = ({ value }) => {
    const item = B2B_OUTCOME_MAP[value] || { label: value, badgeClass: 'bg-gray-100 text-gray-700' };
    return (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black border uppercase tracking-wider ${item.badgeClass}`}>
            {item.label}
        </span>
    );
};

const B2BDealersView = () => {
    const role = getUserRole();
    const canSelectUser = role === 'admin' || role === 'staff';
    const [loading, setLoading] = useState(false);
    const [dealers, setDealers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(canSelectUser ? '' : getUserId());
    const [staffOptions, setStaffOptions] = useState([]);
    const [search, setSearch] = useState('');
    const [detailLead, setDetailLead] = useState(null);
    const [logModal, setLogModal] = useState({ open: false, lead: null, completeFollowUpId: null });
    const [viewingCardUrl, setViewingCardUrl] = useState(null);

    const fetchDealers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (canSelectUser && selectedUser) params.set('user_id', selectedUser);
            if (search) params.set('search', search);
            const res = await getB2BMyDealers(params.toString());
            if (res.status === 200) {
                setDealers(res.data.leads || res.data.results || res.data || []);
            } else {
                toast.error(res?.data?.detail || "Failed to fetch dealers");
            }
        } catch (error) {
            toast.error("Error fetching dealers");
        } finally {
            setLoading(false);
        }
    }, [canSelectUser, selectedUser, search]);

    const handleVisitingCardUpload = async (leadId, file) => {
        if (!file) return;
        const toastId = toast.loading("Uploading visiting card...");
        try {
            const formData = new FormData();
            formData.append('visiting_card', file);
            const res = await updateB2BLead(leadId, formData);
            if (res.status === 200) {
                toast.update(toastId, { render: "Visiting card uploaded successfully", type: "success", isLoading: false, autoClose: 3000 });
                fetchDealers();
            } else {
                toast.update(toastId, { render: "Failed to upload visiting card", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (error) {
            console.error("Error uploading visiting card:", error);
            toast.update(toastId, { render: "Failed to upload visiting card", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleToggleDealer = useCallback(async (item) => {
        const assignmentId = item.assigned_to?.assignment_id;
        if (!assignmentId) {
            toast.error("Assignment ID not found for this lead");
            return;
        }
        const currentStatus = item.assigned_to?.is_my_dealer || false;
        const newStatus = !currentStatus;
        
        try {
            const res = await toggleB2BDealerStatus(assignmentId, newStatus);
            if (res.status === 200 || res.status === 204) {
                toast.success(`Successfully ${newStatus ? 'marked as' : 'unmarked from'} My Dealers`);
                fetchDealers();
            } else {
                toast.error(res.data?.detail || "Failed to update dealer status");
            }
        } catch (error) {
            toast.error("Error updating dealer status");
        }
    }, [fetchDealers]);

    const fetchStaff = async () => {
        if (!canSelectUser) return;
        try {
            const res = await getB2BSalespersonList();
            if (res.status === 200) {
                const list = res.data.results || res.data || [];
                const options = list.map(user => ({
                    label: user.name || user.username || user.email,
                    value: user.id,
                }));
                setStaffOptions(options);
                if (options.length > 0 && !selectedUser) {
                    setSelectedUser(options[0].value);
                }
            }
        } catch (error) {
            console.error("Error fetching salespeople:", error);
        }
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    useEffect(() => {
        fetchDealers();
    }, [fetchDealers]);

    const headers = [
        { label: "Date", key: "created_at", width: "85px" },
        { label: "Shop/Owner", key: "shop_owner" },
        { label: "Phone", key: "phone", width: "110px" },
        { label: "Product Sub-Category", key: "product_subcategory", width: "130px" },
        { label: "Last Outcome", key: "last_outcome", width: "120px" },
        { label: "District/State", key: "location" },
        { label: "Last Order Date", key: "last_order_date", width: "110px" },
        { label: "Flags", key: "flags", width: "95px" },
        { label: "Actions", key: "actions", width: "140px" },
    ];

    const rows = useMemo(() => dealers.map(item => ({
        created_at: (
            <span className="text-[10px] text-gray-500 font-medium">
                {formatDateMonth(item.created_at)}
            </span>
        ),
        shop_owner: (
            <button onClick={() => setDetailLead(item)} className="text-left w-full">
                <div className="text-xs font-black text-gray-800 hover:text-primary">{item.shop_name || 'Unnamed shop'}</div>
                <div className="text-[10px] text-gray-500">{item.owner_name || 'No owner'}</div>
            </button>
        ),
        phone: (
            <div className="flex flex-col gap-1 items-start">
                {item.phone ? (
                    <button onClick={() => setDetailLead(item)} className="text-sm font-bold text-primary hover:underline text-left">
                        {item.phone}
                    </button>
                ) : '—'}
                <div className="mt-0.5">
                    {item.visiting_card ? (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setViewingCardUrl(item.visiting_card)}
                                className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-gray-200 rounded text-[9px] font-bold text-gray-600 hover:bg-gray-100 transition-all whitespace-nowrap"
                            >
                                <Eye size={10} /> View Card
                            </button>
                            <label className="bg-white border rounded p-0.5 shadow-sm cursor-pointer hover:bg-gray-50 flex items-center justify-center">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handleVisitingCardUpload(item.id, e.target.files[0])}
                                />
                                <svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                </svg>
                            </label>
                        </div>
                    ) : (
                        <label className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 border border-dashed border-gray-300 rounded text-[9px] font-bold text-gray-500 hover:bg-gray-100 hover:border-gray-400 cursor-pointer transition-all whitespace-nowrap">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleVisitingCardUpload(item.id, e.target.files[0])}
                            />
                            <span>+ Visiting Card</span>
                        </label>
                    )}
                </div>
            </div>
        ),
        product_subcategory: (
            <div className="flex flex-wrap gap-1 max-w-[150px]">
                {(item.product_subcategory || item.product_sub_category || item.subcategory || item.sub_category || []).map(sc => (
                    <span key={sc.id || sc} className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-[9px] font-bold">
                        {sc.name || sc}
                    </span>
                ))}
                {!(item.product_subcategory || item.product_sub_category || item.subcategory || item.sub_category || []).length && (
                    <span className="text-[10px] text-gray-400">—</span>
                )}
            </div>
        ),
        last_outcome: (
            <div className="flex flex-col gap-1">
                <div>
                    <OutcomeBadge value={item.last_outcome || 'new'} />
                    {item.last_outcome_comment && (
                        <div className="text-[10px] text-gray-500 italic max-w-[120px] truncate" title={item.last_outcome_comment}>
                            {item.last_outcome_comment}
                        </div>
                    )}
                </div>
                {item.next_follow_up?.follow_up_at && (
                    <div className="text-[10px] font-bold text-gray-500 animate-pulse" title="Next Follow-up">
                        📅 {new Date(item.next_follow_up.follow_up_at).toLocaleDateString([], { dateStyle: 'medium' })}
                    </div>
                )}
            </div>
        ),
        location: (
            <div className="text-xs text-gray-600">
                <div>{item.district_name || item.district || '—'} / {item.state_name || item.state || '—'}</div>
                {item.area && <div className="text-[10px] text-gray-400">{item.area}</div>}
            </div>
        ),
        last_order_date: (
            <span className="text-[10px] font-medium text-gray-600">
                {item.last_order_date || '—'}
            </span>
        ),
        flags: (
            <div className="flex gap-1.5 flex-wrap items-center">
                {item.is_paid && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-black">₹</span>}
                {item.is_urgent && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black">⚡</span>}
                <button
                    onClick={() => handleToggleDealer(item)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-all"
                    title={item.assigned_to?.is_my_dealer ? "Unmark as My Dealer" : "Mark as My Dealer"}
                >
                    <span className="text-sm text-amber-500 animate-pulse font-black">
                        ★
                    </span>
                </button>
            </div>
        ),
        actions: (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setLogModal({ open: true, lead: item, completeFollowUpId: null })} className="px-2 py-1.5 bg-primary text-white rounded-lg text-[10px] font-black flex items-center gap-1"><PhoneCall size={13} /> Log</button>
                <button onClick={() => setDetailLead(item)} className="p-1.5 bg-gray-100 text-gray-600 rounded-full"><Eye size={13} /></button>
            </div>
        ),
        _original: item,
    })), [dealers, handleVisitingCardUpload, handleToggleDealer]);

    return (
        <div className="space-y-3">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100 shadow-sm">
                <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Leads' }, { name: 'My B2B Dealers' }]} />
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-2">
                    <div>
                        <h1 className="text-2xl font-black text-gray-800">My B2B Dealers</h1>
                        <p className="text-xs font-bold text-gray-500">Important contacts list</p>
                    </div>
                    {canSelectUser && (
                        <div className="w-full md:w-72">
                            <Dropdown options={staffOptions} value={selectedUser} onChange={setSelectedUser} placeholder="Viewing: salesperson" needSearch compact />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-2 md:items-center justify-between">
                <SearchBar placeholder="Search B2B dealers..." onSearch={setSearch} value={search} compact />
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                {loading ? (
                    <div className="flex justify-center py-20"><CustomLoading size={60} /></div>
                ) : dealers.length > 0 ? (
                    <Table headers={headers} rows={rows} />
                ) : (
                    <div className="text-center py-20">
                        <p className="text-sm text-gray-400 italic">No important contacts marked as My Dealers yet.</p>
                        <p className="text-xs text-gray-400 mt-1">To add one, go to My B2B Leads and click the ★ star next to any lead's flags.</p>
                    </div>
                )}
            </div>

            {detailLead && <B2BLeadDetail lead={detailLead} onClose={() => setDetailLead(null)} onUpdated={fetchDealers} />}
            <B2BLogInteractionModal
                isOpen={logModal.open}
                onClose={() => setLogModal({ open: false, lead: null, completeFollowUpId: null })}
                lead={logModal.lead}
                completeFollowUpId={logModal.completeFollowUpId}
                onSuccess={fetchDealers}
            />

            {viewingCardUrl && (
                <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/80 p-4" onClick={() => setViewingCardUrl(null)}>
                    <div className="relative max-w-full max-h-full">
                        <img src={viewingCardUrl} alt="Visiting Card" className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl border-4 border-white" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default B2BDealersView;
